import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useProfile, useUpdateProfile } from '@/features/profile/hooks';
import { isDisplayNameAvailable } from '@/lib/data/provider';
import type { Tables } from '@/lib/supabase/types';

type NameStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

const NAME_RE = /^[\p{L}\p{N} _.-]{3,20}$/u;

function AvatarPreview({ uri, initial }: { uri: string; initial: string }) {
  const palette = useThemePalette();
  return (
    <LinearGradient
      colors={[palette.brandMagenta, palette.brandViolet, palette.brandCyan]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={{ width: 100, height: 100, borderRadius: 50, alignItems: 'center', justifyContent: 'center' }}
    >
      <View
        className="items-center justify-center overflow-hidden rounded-full bg-surface"
        style={{ width: 90, height: 90 }}
      >
        {uri ? (
          <Image source={{ uri }} style={{ width: 90, height: 90, borderRadius: 45 }} contentFit="cover" cachePolicy="memory-disk" />
        ) : (
          <AppText variant="display" weight="bold" style={{ color: palette.voltBright }}>
            {initial}
          </AppText>
        )}
      </View>
    </LinearGradient>
  );
}

/** The form proper - mounted only once the profile is loaded, so useState seeds
 *  cleanly from props without a sync-in-effect seeding pass. */
function EditForm({ profile }: { profile: Tables<'profiles'> }) {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const toast = useToast();
  const updateProfile = useUpdateProfile();

  const original = profile.display_name;
  const [name, setName] = useState(original);
  // Async result cache, keyed by the exact name it was checked for.
  const [checked, setChecked] = useState<{ name: string; available: boolean } | null>(null);

  const trimmed = name.trim();
  const unchanged = trimmed.toLowerCase() === original.toLowerCase();
  const valid = NAME_RE.test(trimmed);

  // Derive status during render - no synchronous setState in an effect.
  const status: NameStatus = unchanged
    ? 'idle'
    : !valid
      ? 'invalid'
      : checked?.name === trimmed
        ? checked.available
          ? 'available'
          : 'taken'
        : 'checking';

  // Async availability check only; setState happens inside the debounce/promise.
  useEffect(() => {
    if (unchanged || !valid || checked?.name === trimmed) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      isDisplayNameAvailable(trimmed)
        .then((available) => {
          if (!cancelled) setChecked({ name: trimmed, available });
        })
        .catch(() => undefined);
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [trimmed, unchanged, valid, checked]);

  const nameError =
    status === 'invalid'
      ? t('profile.usernameInvalid')
      : status === 'taken'
        ? t('profile.usernameTaken')
        : undefined;

  const canSave =
    !updateProfile.isPending &&
    trimmed.length >= 3 &&
    !unchanged &&
    status !== 'taken' &&
    status !== 'invalid' &&
    status !== 'checking';

  const onSave = () => {
    updateProfile.mutate(
      {
        display_name: trimmed,
        display_name_confirmed: true,
      },
      {
        onSuccess: () => {
          toast.show({ message: t('profile.savedToast'), tone: 'success' });
          router.back();
        },
        onError: (error) => {
          if (error instanceof Error && error.message === 'USERNAME_TAKEN') {
            setChecked({ name: trimmed, available: false });
          } else {
            toast.show({ message: t('common.errorTitle'), tone: 'error' });
          }
        },
      },
    );
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 40, gap: 22 }}>
      <View className="items-center gap-2">
        <AvatarPreview uri={profile.avatar_url ?? ''} initial={(name || '?').charAt(0).toUpperCase()} />
        <AppText variant="micro" className="text-muted">
          {t('profile.avatarSynced')}
        </AppText>
      </View>

      <View className="gap-1.5">
        <TextField
          label={t('profile.username')}
          value={name}
          onChangeText={setName}
          placeholder={t('profile.usernamePlaceholder')}
          autoCapitalize="none"
          maxLength={20}
          errorText={nameError}
        />
        {status === 'checking' ? (
          <AppText variant="micro" className="text-muted">
            {t('profile.usernameChecking')}
          </AppText>
        ) : status === 'available' ? (
          <View className="flex-row items-center gap-1">
            <Ionicons name="checkmark-circle" size={13} color={palette.win} />
            <AppText variant="micro" style={{ color: palette.win }}>
              {t('profile.usernameAvailable')}
            </AppText>
          </View>
        ) : null}
      </View>

      <Button
        label={t('common.save')}
        onPress={onSave}
        disabled={!canSave}
        loading={updateProfile.isPending}
      />
    </ScrollView>
  );
}

export default function EditProfileScreen() {
  const { t } = useTranslation();
  const profileQuery = useProfile();

  return (
    <Screen>
      <BackHeader title={t('profile.editTitle')} />
      {profileQuery.data ? (
        <EditForm profile={profileQuery.data} />
      ) : (
        <View className="gap-4 p-5">
          <Skeleton className="mx-auto h-24 w-24 rounded-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </View>
      )}
    </Screen>
  );
}
